import React, { useCallback, useEffect, useState } from "react";
import { ContentLayout, Header } from "@amzn/awsui-components-react-v3/polaris";
import { PageLayout } from "../components/page-layout";
import { useDirectUsersQuery } from "../services/idp/idp";
import { useAuth } from "react-oidc-context";
import { DirectsTable } from "../components/directs-table";
import { PagePaths } from "../lib/constants";
import { Breadcrumbs } from "../components/breadcrumbs";
import { Alerts as AlertsType } from "../lib/types";
import { Alerts } from "../components/alerts";
import { formatMessageFromResponseErrorData } from "../utils/errors";

const breadcrumbItems = [
  { href: "#", text: "User Management" },
  { href: PagePaths.MANAGE_DIRECTS, text: "Your team" },
];

export const ManageDirects = () => {
  const auth = useAuth();
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [activePanel, setActivePanel] = useState<React.ReactNode>(null);
  const [alerts, setAlerts] = useState<AlertsType>({});
  const { data, isFetching, isError, error } = useDirectUsersQuery({
    includeDisabled,
  });

  const openHelpPanel = (panel: React.ReactNode) => {
    setActivePanel(panel);
    setToolsOpen(true);
  };

  const handleDismissAlert = useCallback((key: string) => {
    setAlerts((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    if (isError) {
      setAlerts((prev) => ({
        ...prev,
        fetchDirectsError: {
          message: formatMessageFromResponseErrorData((error as any)?.data),
          ariaLabel: "Error",
          type: "error",
        },
      }));
    }
  }, [isFetching, isError, error]);

  return (
    <PageLayout
      auth={auth}
      tools={activePanel}
      toolsOpen={toolsOpen}
      onToolsChange={setToolsOpen}
    >
      <ContentLayout
        headerVariant="high-contrast"
        header={
          <Header variant="h1" description="Manage your direct reports">
            Team Management
          </Header>
        }
        notifications={<Alerts items={alerts} onDismiss={handleDismissAlert} />}
        defaultPadding
        data-testid="admin-page"
        breadcrumbs={<Breadcrumbs items={breadcrumbItems} />}
      >
        <DirectsTable
          users={data?.users}
          loading={isFetching}
          showDisabled={includeDisabled}
          onShowDisabledChange={setIncludeDisabled}
          onInfoClick={openHelpPanel}
          setAlerts={setAlerts}
        />
      </ContentLayout>
    </PageLayout>
  );
};
